/**
 * SSO Service
 * Swagger: /sso/*
 * 
 * SSO (Single Sign-On) 관련 API 호출
 * - UserSsoAccess 정보 조회
 * - SSO 로그인
 */

import { apiClient } from './apiClient';
import type {
  SsoUserInfoResponseDto,
  SsoLoginRequestDto,
  SsoLoginResponseDto,
  SsoConnectRequestDto,
  SsoConnectResponseDto,
  SsoSignupRequestDto,
  SsoSignupResponseDto,
} from '../types/api/sso';

/**
 * GET /sso/:userSsoAccessId
 * UserSsoAccess 정보 조회
 * 
 * @param userSsoAccessId - UserSsoAccess ID
 * @returns SSO 접근 정보 및 연동된 사용자 정보
 * 
 * @description
 * UserSsoAccessId로 SSO 접근 정보를 조회합니다.
 * 연동된 사용자 정보(user)와 SSO 정보(userSsoAccess)를 별도 객체로 반환합니다.
 */
export const getSsoInfo = async (
  userSsoAccessId: number
): Promise<SsoUserInfoResponseDto> => {
  return apiClient.get<SsoUserInfoResponseDto>(
    `/sso/${userSsoAccessId}`,
    { skipAuth: true }
  );
};

/**
 * POST /sso/login
 * SSO 로그인
 * 
 * @param data - UserSsoAccessId
 * @returns 액세스 토큰, 리프레시 토큰, 사용자 ID
 * 
 * @description
 * UserSsoAccessId로 연결된 사용자의 액세스 토큰과 리프레시 토큰을 발급합니다.
 */
export const ssoLogin = async (
  data: SsoLoginRequestDto
): Promise<SsoLoginResponseDto> => {
  return apiClient.post<SsoLoginResponseDto>(
    '/sso/login',
    data,
    { skipAuth: true }
  );
};

/**
 * PATCH /sso/connect
 * 기존 계정에 SSO 연동
 * 
 * @param data - UserSsoAccess ID와 연동할 사용자 ID
 * @returns 연동 성공 여부 및 ID 정보
 * 
 * @description
 * UserSsoAccessId와 userId를 받아 기존 계정에 SSO를 연동합니다.
 */
export const connectSso = async (
  data: SsoConnectRequestDto
): Promise<SsoConnectResponseDto> => {
  return apiClient.patch<SsoConnectResponseDto>(
    '/sso/connect',
    data,
    { skipAuth: true }
  );
};

/**
 * POST /sso/signup
 * SSO 회원가입
 * 
 * @param data - 회원가입 정보 (userSsoAccessId, email, realName, loginId, pw)
 * @returns 생성된 사용자 ID
 * 
 * @description
 * UserSsoAccessId로 새 계정을 생성하고 SSO 정보를 연동합니다.
 */
export const ssoSignup = async (
  data: SsoSignupRequestDto
): Promise<SsoSignupResponseDto> => {
  return apiClient.post<SsoSignupResponseDto>(
    '/sso/signup',
    data,
    { skipAuth: true }
  );
};
